import React from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, Button, ButtonGroup } from '@/js/libs/ui';

import type { ModalPayload } from 'src/modules/ConfirmModal/types.ts';
import * as actions from './actions.tsx';
import context, { api } from './Context.tsx';
import * as selectors from './selectors.tsx';

export const ConfirmationModal: React.FC = () => {
  const [t] = useTranslation(['common']);
  const [pending, setPending] = React.useState<boolean>(false);
  const [state, dispatch] = React.useContext(context);

  const visible = selectors.isOpened(state);
  const title = selectors.getTitle(state) ?? t('common:CONFIRM');
  const text = selectors.getText(state) ?? t('common:SURE_TO_PROCEED');
  const confirmText = selectors.getConfirmButtonTitle(state) ?? t('common:CONFIRM');
  const cancelText = selectors.getCancelButtonTitle(state) ?? t('common:BUTTON_CANCEL');
  const guardedFunction = selectors.getGuardedFunction(state);

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const handleClose = React.useCallback(() => {
    dispatch(actions.close());
  }, [dispatch]);

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-restricted-syntax
  const handleExecute = React.useCallback(async () => {
    if (guardedFunction) {
      setPending(true);
      await guardedFunction();
      setPending(false);
      dispatch(actions.close());
    }
  }, [guardedFunction, dispatch]);

  return (
    <Modal isOpen={visible} onRequestClose={handleClose} title={title}>
      <Modal.Body>
        <div>{text}</div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={handleClose} variant='create-secondary' dataTestId='cancel-button'>
            {cancelText}
          </Button>
          <Button type='submit' disabled={pending} isLoading={pending} onClick={handleExecute} data-testid='approve-confirm-modal'>
            {confirmText}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

type OpenModal = (payload: ModalPayload) => void;
type CloseModal = () => void;
type UseConfirmModalReturn = {
  open: OpenModal;
  close: CloseModal;
};
type UseConfirmModal = () => UseConfirmModalReturn;

export const useConfirmModal: UseConfirmModal = () => {
  const [, dispatch] = React.useContext(context);
  return React.useMemo<UseConfirmModalReturn>(
    () => ({
      open: (payload: ModalPayload) => dispatch(actions.open(payload)),
      close: () => dispatch(actions.close()),
    }),
    [dispatch],
  );
};

export default api;
